'use client';

import clsx from 'clsx';
import { X } from 'lucide-react';
import {
  ComponentRef,
  Fragment,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

export type DialogOpenButtonProps =
  | { removeOpenButton: true }
  | {
      removeOpenButton?: false;
      openButtonContent: ReactNode;
      openButtonClass?: string;
    };

export function Dialog({
  children,
  contentClass,
  onClose,
  forceClose,
  disableBackdropClose,
  dataTheme,
  ...restProps
}: PropsWithChildren<
  {
    contentClass?: string;
    onClose?: () => void;
    forceClose?: boolean;
    disableBackdropClose?: boolean;
    dataTheme?: string;
  } & DialogOpenButtonProps
>) {
  const [documentReady, setDocumentReady] = useState(false);

  const dialogRef = useRef<ComponentRef<'dialog'>>(null);
  const contentRef = useRef<ComponentRef<'div'>>(null);

  useEffect(() => {
    setDocumentReady(true);
  }, []);

  useEffect(() => {
    if (!documentReady) return;

    const controller = new AbortController();

    if (restProps.removeOpenButton && !dialogRef.current?.open) {
      openDialog();
    }

    if (disableBackdropClose) return;

    const handleClickOutside = (event: MouseEvent) => {
      const clickedElement = event.target as HTMLElement;
      const isWithinAnyDialog = clickedElement.closest('dialog[open]');

      if (
        dialogRef.current?.contains(clickedElement) &&
        !contentRef.current?.contains(clickedElement) &&
        (!isWithinAnyDialog || isWithinAnyDialog === dialogRef.current)
      ) {
        closeDialog();
      }
    };

    document.addEventListener('mousedown', handleClickOutside, { signal: controller.signal });

    return () => {
      controller.abort();
    };
  }, [dialogRef, contentRef, documentReady]);

  useEffect(() => {
    if (forceClose) {
      closeDialog();
    }
  }, [forceClose]);

  function openDialog() {
    dialogRef.current?.showModal();
  }

  function closeDialog() {
    onClose?.();
    dialogRef.current?.close();
  }

  // Only access document when component is mounted on client
  if (!documentReady) return null;

  return (
    <Fragment>
      {!restProps.removeOpenButton && (
        <button type="button" className={restProps.openButtonClass} onClick={openDialog}>
          {restProps.openButtonContent}
        </button>
      )}
      {createPortal(
        <dialog data-theme={dataTheme} ref={dialogRef} className="modal">
          <div ref={contentRef} className={clsx('modal-box pt-0!', contentClass)}>
            <div className="bg-base-100 z-1 sticky top-0 flex justify-end py-2">
              <button
                type="button"
                className="btn btn-sm btn-circle btn-ghost"
                onClick={closeDialog}
              >
                <X />
              </button>
            </div>
            {children}
          </div>
        </dialog>,
        document.body,
      )}
    </Fragment>
  );
}
